import type { GameState, OnboardingAction, OnboardingStep } from "../domain/types";
import type { Translator } from "../i18n/locales";
import { GAME_CONFIG } from "../config/gameConfig";
import type { GuidePose } from "../phaser/OnboardingGuideScene";
import { formatSeasonCountdown, formatUsdtCents, formatUtcDate } from "./formatters";
import { escapeHtml } from "./templates";

export type OnboardingUiModel = {
  step: OnboardingStep;
  pose: GuidePose;
  kicker: string;
  title: string;
  body: string;
  progress: string | null;
  progressValue: string | null;
  progressRatio: number;
  targetLabel: string | null;
  primaryLabel: string | null;
  primaryAction: OnboardingAction | null;
  secondaryLabel: string | null;
  seasonHero: { label: string; value: string } | null;
  seasonSummary: { fund: string; time: string; rule: string } | null;
  seasonMetrics: { placeLabel: string; place: string; shareLabel: string; share: string; rewardLabel: string; reward: string } | null;
};

function clampRatio(value: number): number {
  return Math.max(0, Math.min(1, value));
}

export function onboardingUiModel(state: GameState, serverNow: string, t: Translator): OnboardingUiModel {
  const locale = state.settings.locale;
  const step = state.onboarding.step;
  const common = {
    step,
    kicker: t("onboarding.keeperName"),
    progress: null,
    progressValue: null,
    progressRatio: 0,
    targetLabel: null,
    primaryLabel: null,
    primaryAction: null,
    secondaryLabel: null,
    seasonHero: null,
    seasonSummary: null,
    seasonMetrics: null,
  };
  if (step === "welcome") {
    const scheduled = state.season.status === "scheduled";
    return {
      ...common,
      pose: "welcome",
      kicker: t("onboarding.welcomeKicker"),
      title: t("onboarding.welcomeTitle"),
      body: t("onboarding.welcomeBody"),
      primaryLabel: t("onboarding.begin"),
      primaryAction: "begin",
      secondaryLabel: t("onboarding.skip"),
      seasonHero: {
        label: t(scheduled ? "header.seasonStarts" : "onboarding.seasonFundLabel"),
        value: scheduled
          ? formatSeasonCountdown(state.season.startsAt, serverNow, locale)
          : formatUsdtCents(state.season.prizeFundUsdCents, locale),
      },
      seasonSummary: {
        fund: "",
        time: scheduled
          ? formatUtcDate(state.season.startsAt, locale)
          : t("onboarding.seasonTime", {
              time: formatSeasonCountdown(state.season.endsAt, serverNow, locale),
            }),
        rule: t("onboarding.seasonRule"),
      },
    };
  }
  if (step === "tap") {
    const current = Math.max(0, state.progress.confirmedTaps - state.onboarding.tapBaseline);
    return {
      ...common,
      pose: "tap",
      title: t("onboarding.tapTitle"),
      body: t("onboarding.tapBody"),
      progress: t("onboarding.tapProgress", { current: Math.min(current, state.onboarding.tapTarget), target: state.onboarding.tapTarget }),
      progressRatio: clampRatio(current / Math.max(1, state.onboarding.tapTarget)),
      targetLabel: t("core.tapButton"),
    };
  }
  if (step === "map") {
    return {
      ...common,
      pose: "map",
      title: t("onboarding.mapTitle"),
      body: t("onboarding.mapBody"),
      targetLabel: t("onboarding.mapAction"),
    };
  }
  if (step === "workshop") {
    return {
      ...common,
      pose: "workshop",
      title: t("onboarding.workshopTitle"),
      body: t("onboarding.workshopBody"),
      targetLabel: t("onboarding.workshopAction"),
    };
  }
  if (step === "upgrade") {
    return {
      ...common,
      pose: "upgrade",
      title: t("onboarding.upgradeTitle"),
      body: t("onboarding.upgradeBody"),
      targetLabel: t("onboarding.upgradeAction"),
    };
  }
  if (step === "passive") {
    const reward = GAME_CONFIG.onboarding.firstOutputReward;
    return {
      ...common,
      pose: "passive",
      title: t("onboarding.passiveTitle"),
      body: t("onboarding.passiveBody", { value: reward }),
      primaryLabel: t("onboarding.passiveClaim", { value: reward }),
      primaryAction: "claim-first-output",
    };
  }
  if (step === "trophy") {
    return {
      ...common,
      pose: "trophy",
      title: t("onboarding.trophyTitle"),
      body: t("onboarding.trophyBody"),
      targetLabel: t("onboarding.trophyAction"),
    };
  }
  if (step === "trophy-overview") {
    return {
      ...common,
      pose: "trophy",
      title: t("onboarding.trophyOverviewTitle"),
      body: t("onboarding.trophyOverviewBody"),
      primaryLabel: t("onboarding.trophyOverviewAction"),
      primaryAction: "explain-trophies",
    };
  }
  if (step === "season") {
    return {
      ...common,
      pose: "season",
      title: t("onboarding.seasonTitle"),
      body: t("onboarding.seasonBody"),
      targetLabel: t("onboarding.seasonAction"),
    };
  }
  if (step === "season-overview") {
    return {
      ...common,
      pose: "seasonReturn",
      title: t("onboarding.seasonOverviewTitle"),
      body: t("onboarding.seasonOverviewBody"),
      primaryLabel: t("onboarding.next"),
      primaryAction: "explain-season",
      seasonSummary: {
        fund: t("onboarding.seasonFund", { fund: formatUsdtCents(state.season.prizeFundUsdCents, locale) }),
        time: t("onboarding.seasonTime", { time: formatSeasonCountdown(state.season.endsAt, serverNow, locale) }),
        rule: t("onboarding.seasonNext"),
      },
    };
  }
  if (step === "season-return") {
    return {
      ...common,
      pose: "club",
      title: t("onboarding.seasonReturnTitle"),
      body: t("onboarding.seasonReturnBody"),
      targetLabel: t("onboarding.returnMapAction"),
    };
  }
  if (step === "club") {
    return {
      ...common,
      pose: "club",
      title: t("onboarding.clubTitle"),
      body: t("onboarding.clubBody"),
      targetLabel: t("onboarding.clubAction"),
    };
  }
  if (step === "club-overview") {
    const percent = new Intl.NumberFormat(locale, { style: "percent", maximumFractionDigits: 0 });
    return {
      ...common,
      pose: "clubReturn",
      title: t("onboarding.clubOverviewTitle"),
      body: t("onboarding.clubOverviewBody", {
        levelOne: percent.format(GAME_CONFIG.referrals.levelOneRateBasisPoints / 10_000),
        levelTwo: percent.format(GAME_CONFIG.referrals.levelTwoRateBasisPoints / 10_000),
      }),
      primaryLabel: t("onboarding.clubOverviewAction"),
      primaryAction: "explain-club",
    };
  }
  if (step === "club-link") {
    return {
      ...common,
      pose: "club",
      title: t("onboarding.clubLinkTitle"),
      body: t("onboarding.clubLinkBody"),
      targetLabel: t("onboarding.clubLinkAction"),
    };
  }
  if (step === "club-return") {
    return {
      ...common,
      pose: "map",
      title: t("onboarding.clubReturnTitle"),
      body: t("onboarding.clubReturnBody"),
      targetLabel: t("onboarding.returnMapAction"),
    };
  }
  if (step === "arena") {
    return {
      ...common,
      pose: "arena",
      title: t("onboarding.arenaTitle"),
      body: t("onboarding.arenaBody"),
      targetLabel: t("onboarding.arenaAction"),
    };
  }
  if (step === "arena-overview") {
    return {
      ...common,
      pose: "welcome",
      title: t("onboarding.arenaOverviewTitle"),
      body: t("onboarding.arenaOverviewBody"),
      primaryLabel: t("onboarding.arenaOverviewAction"),
      primaryAction: "explain-arena",
    };
  }
  if (step === "arena-game") {
    return {
      ...common,
      pose: "arena",
      title: t("onboarding.arenaGameTitle"),
      body: t("onboarding.arenaGameBody"),
      targetLabel: t("onboarding.arenaGameTarget"),
      primaryLabel: t("onboarding.arenaGameAction"),
      primaryAction: "return-from-arena",
    };
  }
  if (step === "arena-return") {
    return {
      ...common,
      pose: "seasonReturn",
      title: t("onboarding.arenaReturnTitle"),
      body: t("onboarding.arenaReturnBody"),
      primaryLabel: t("onboarding.arenaReturnAction"),
      primaryAction: "return-from-arena",
    };
  }
  return {
    ...common,
    pose: "complete",
    kicker: t("onboarding.completeKicker"),
    title: t("onboarding.completeTitle"),
    body: t("onboarding.completeBody"),
    progressRatio: 1,
    primaryLabel: t("onboarding.finish"),
    primaryAction: "finish",
  };
}

export function renderOnboardingLayer(state: GameState, serverNow: string, t: Translator): string {
  const model = onboardingUiModel(state, serverNow, t);
  return `<div class="onboarding-layer" data-onboarding-step="${model.step}" data-onboarding-target="false" hidden>
    <div class="onboarding-dim" aria-hidden="true"></div>
    <div class="onboarding-focus-clone" aria-hidden="true" hidden></div>
    <button class="onboarding-spotlight" type="button" data-action="onboarding-target" aria-label="${escapeHtml(
      model.targetLabel ?? model.title,
    )}" hidden></button>
    <div class="onboarding-pointer-stage" id="onboarding-pointer-runtime" aria-hidden="true"></div>
    <div class="onboarding-guide-stage" id="onboarding-guide-runtime" aria-hidden="true"></div>
    <section class="onboarding-dialog" role="dialog" aria-modal="true" aria-labelledby="onboarding-title" aria-describedby="onboarding-body">
      <span class="onboarding-dialog__speaker" data-onboarding-kicker>${escapeHtml(model.kicker)}</span>
      <h2 id="onboarding-title" data-onboarding-title aria-live="polite">${escapeHtml(model.title)}</h2>
      <p id="onboarding-body" data-onboarding-body>${escapeHtml(model.body)}</p>
      <div class="onboarding-season-hero" data-onboarding-season-hero ${model.seasonHero === null ? "hidden" : ""}>
        <span data-onboarding-season-hero-label>${escapeHtml(model.seasonHero?.label ?? "")}</span>
        <strong data-onboarding-season-hero-value>${escapeHtml(model.seasonHero?.value ?? "")}</strong>
      </div>
      <div class="onboarding-season-summary" data-onboarding-season ${model.seasonSummary === null ? "hidden" : ""}>
        <strong data-onboarding-fund>${escapeHtml(model.seasonSummary?.fund ?? "")}</strong>
        <span data-onboarding-time>${escapeHtml(model.seasonSummary?.time ?? "")}</span>
        <p data-onboarding-rule>${escapeHtml(model.seasonSummary?.rule ?? "")}</p>
      </div>
      <div class="onboarding-season-metrics" data-onboarding-season-metrics ${model.seasonMetrics === null ? "hidden" : ""}>
        <span><small data-onboarding-place-label>${escapeHtml(model.seasonMetrics?.placeLabel ?? "")}</small><strong data-onboarding-place>${escapeHtml(model.seasonMetrics?.place ?? "")}</strong></span>
        <span><small data-onboarding-share-label>${escapeHtml(model.seasonMetrics?.shareLabel ?? "")}</small><strong data-onboarding-share>${escapeHtml(model.seasonMetrics?.share ?? "")}</strong></span>
        <span><small data-onboarding-reward-label>${escapeHtml(model.seasonMetrics?.rewardLabel ?? "")}</small><strong data-onboarding-reward>${escapeHtml(model.seasonMetrics?.reward ?? "")}</strong></span>
      </div>
      <div class="onboarding-progress" data-onboarding-progress data-emphasis="${model.progressValue !== null}" ${model.progress === null ? "hidden" : ""}>
        <strong data-onboarding-progress-value ${model.progressValue === null ? "hidden" : ""}>${escapeHtml(model.progressValue ?? "")}</strong>
        <span data-onboarding-progress-label aria-live="polite">${escapeHtml(model.progress ?? "")}</span>
        <i role="progressbar" aria-valuemin="0" aria-valuemax="100" aria-valuenow="${Math.round(model.progressRatio * 100)}"><b style="--onboarding-progress:${model.progressRatio}"></b></i>
      </div>
      <div class="onboarding-dialog__actions" data-onboarding-actions ${model.primaryLabel === null ? "hidden" : ""}>
        <button class="onboarding-primary" type="button" data-action="onboarding-primary" data-onboarding-command="${model.primaryAction ?? ""}">${escapeHtml(
          model.primaryLabel ?? "",
        )}</button>
        <button class="onboarding-skip" type="button" data-action="onboarding-skip" ${model.secondaryLabel === null ? "hidden" : ""}>${escapeHtml(
          model.secondaryLabel ?? "",
        )}</button>
      </div>
    </section>
  </div>`;
}
